import type { FactsetChartParameters } from "./chartCreatorTypes";

export const MAX_ACTIVE_GRAPH_XML_BYTES = 8 * 1024 * 1024;
const FDS_NAMESPACE = "http://xml.factset.com/chart";

export interface ActiveGraphSafeState {
  chartType?: string;
  tickers: string[];
  companyNames: string[];
  startDate?: string;
  endDate?: string;
  frequency?: string;
  stylesetId?: string;
}

export function assertActiveGraphXml(xml: string, label: string): void {
  const trimmed = xml.trim();
  const rootTag = /^<FDSChart(?:\s[^>]*)?>/i.exec(trimmed)?.[0];
  const hasFactSetNamespace =
    rootTag !== undefined &&
    new RegExp(
      `\\sxmlns\\s*=\\s*(["'])${escapeRegExp(FDS_NAMESPACE)}\\1`,
      "i",
    ).test(rootTag);
  const hasCompleteRoot =
    /<\/FDSChart>\s*$/i.test(trimmed) ||
    (rootTag?.endsWith("/>") === true && rootTag.length === trimmed.length);
  if (!rootTag || !hasFactSetNamespace || !hasCompleteRoot) {
    throw new Error(`${label} is not a FactSet FDSChart XML document.`);
  }
  if (/<!DOCTYPE/i.test(trimmed)) {
    throw new Error(`${label} contains a prohibited DOCTYPE.`);
  }
  if (new TextEncoder().encode(trimmed).byteLength > MAX_ACTIVE_GRAPH_XML_BYTES) {
    throw new Error(`${label} exceeds the ActiveGraph XML size limit.`);
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function readProperty(xml: string, name: string): string | undefined {
  const match = new RegExp(
    `<Property>\\s*${escapeRegExp(name)}\\s*=\\s*([^<]*)</Property>`,
    "i",
  ).exec(xml);
  return match?.[1] ? decodeXmlText(match[1].trim()) || undefined : undefined;
}

function readTemplateVariable(xml: string, id: string): string | undefined {
  const direct = new RegExp(
    `<Var\\s+ID\\s*=\\s*["']${escapeRegExp(id)}["']\\s*>([^<]*)</Var>`,
    "i",
  ).exec(xml);
  if (direct?.[1]) return decodeXmlText(direct[1].trim()) || undefined;
  const escaped = new RegExp(
    `&lt;Var\\s+ID=(?:&quot;|")${escapeRegExp(id)}(?:&quot;|")&gt;([^&<]*)&lt;/Var&gt;`,
    "i",
  ).exec(xml);
  return escaped?.[1]
    ? decodeXmlText(escaped[1].trim()) || undefined
    : undefined;
}

function readIndexedProperties(xml: string, prefix: string): string[] {
  const matches = [
    ...xml.matchAll(
      new RegExp(
        `<Property>\\s*${escapeRegExp(prefix)}(\\d+)\\s*=\\s*([^<]*)</Property>`,
        "gi",
      ),
    ),
  ];
  return matches
    .sort((left, right) => Number(left[1]) - Number(right[1]))
    .map((match) => decodeXmlText(match[2].trim()))
    .filter(Boolean);
}

export function parseActiveGraphSafeState(xml: string): ActiveGraphSafeState {
  assertActiveGraphXml(xml, "ActiveGraph XML");
  const propertyTickers = readIndexedProperties(xml, "ID");
  const templateTickers = Array.from(
    { length: 16 },
    (_value, index) => readTemplateVariable(xml, `id${index + 1}`),
  ).filter((ticker): ticker is string => Boolean(ticker));
  return {
    chartType:
      readProperty(xml, "CHART_TYPE") ??
      readTemplateVariable(xml, "CHART_TYPE"),
    tickers:
      propertyTickers.length > 0
        ? propertyTickers
        : templateTickers,
    companyNames: readIndexedProperties(xml, "NAME"),
    startDate:
      readTemplateVariable(xml, "sd1") ??
      readProperty(xml, "STARTDATE"),
    endDate:
      readTemplateVariable(xml, "ed1") ??
      readProperty(xml, "ENDDATE"),
    frequency:
      readTemplateVariable(xml, "frq1") ??
      readProperty(xml, "FREQ"),
    stylesetId: readTemplateVariable(xml, "CHART_STYLE"),
  };
}

function decodeXmlText(value: string): string {
  return value
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&amp;/gi, "&");
}

export function readActiveGraphTitle(xml: string): string | undefined {
  assertActiveGraphXml(xml, "ActiveGraph XML");
  const match = /<Attr>\s*Heading1Text\s*=\s*([^<]*)<\/Attr>/i.exec(xml);
  return match?.[1] ? decodeXmlText(match[1].trim()) : undefined;
}

export function normalizeActiveGraphTitle(title: string): string {
  return Array.from(title.trim()).slice(0, 200).join("");
}

export function applyActiveGraphTitleOverride(
  xml: string,
  title: string,
): { xml: string; applied: boolean } {
  assertActiveGraphXml(xml, "ActiveGraph XML");
  const safeTitle = normalizeActiveGraphTitle(title)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  if (!safeTitle) return { xml, applied: false };
  const pattern = /(<Attr>\s*Heading1Text\s*=\s*)[^<]*(<\/Attr>)/i;
  if (!pattern.test(xml)) return { xml, applied: false };
  return {
    xml: xml.replace(
      pattern,
      (_match, prefix: string, suffix: string) =>
        `${prefix}${safeTitle}${suffix}`,
    ),
    applied: true,
  };
}

export function mergeActiveGraphParameterDelta(
  current: ActiveGraphSafeState,
  delta: Partial<FactsetChartParameters>,
): FactsetChartParameters {
  if (!current.chartType || current.tickers.length === 0) {
    throw new Error("Selected ActiveGraph parameters could not be read safely.");
  }
  return {
    companyNames:
      current.companyNames.length === current.tickers.length
        ? { single: current.companyNames }
        : undefined,
    startDate: current.startDate,
    endDate: current.endDate,
    frequency: current.frequency,
    stylesetId: current.stylesetId,
    ...delta,
    chartType: delta.chartType ?? current.chartType,
    tickers: delta.tickers ?? current.tickers,
  };
}
